import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TaskVersion, TaskVersionPayload } from '../../types';
import { taskVersionApi } from '../../services/taskVersion';

interface TaskVersionState {
  list: TaskVersion[];
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: TaskVersionState = {
  list: [],
  loading: false,
  saving: false,
  error: null,
};

export const fetchTaskVersions = createAsyncThunk(
  'taskVersion/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const res = await taskVersionApi.getTaskVersions();
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.message || '获取任务版本列表失败');
    }
  }
);

export const saveTaskVersion = createAsyncThunk(
  'taskVersion/save',
  async (payload: TaskVersionPayload, { rejectWithValue }) => {
    try {
      const res = await taskVersionApi.saveTaskVersion(payload);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.message || '保存任务版本失败');
    }
  }
);

export const setTaskVersionEnabled = createAsyncThunk(
  'taskVersion/setEnabled',
  async (
    { taskType, enabled }: { taskType: number; enabled: boolean },
    { rejectWithValue }
  ) => {
    try {
      const res = await taskVersionApi.setTaskVersionEnabled(taskType, enabled);
      // 接口返回的是字符串消息，需要重新获取列表来更新状态
      return { taskType, enabled, message: res.data };
    } catch (e: any) {
      return rejectWithValue(e.message || '更新任务版本启用状态失败');
    }
  }
);

const taskVersionSlice = createSlice({
  name: 'taskVersion',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskVersions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTaskVersions.fulfilled,
        (state, action: PayloadAction<TaskVersion[]>) => {
          state.loading = false;
          state.list = action.payload;
        }
      )
      .addCase(fetchTaskVersions.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || '获取任务版本列表失败';
      })
      .addCase(saveTaskVersion.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        saveTaskVersion.fulfilled,
        (state, action: PayloadAction<TaskVersion>) => {
          state.saving = false;
          const idx = state.list.findIndex(
            (item) => item.taskType === action.payload.taskType
          );
          if (idx >= 0) {
            state.list[idx] = action.payload;
          } else {
            state.list.push(action.payload);
          }
        }
      )
      .addCase(saveTaskVersion.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || '保存任务版本失败';
      })
      .addCase(setTaskVersionEnabled.pending, (state) => {
        state.error = null;
      })
      .addCase(
        setTaskVersionEnabled.fulfilled,
        (state, action: PayloadAction<{ taskType: number; enabled: boolean; message: string }>) => {
          // 更新列表中对应项的 enabled 状态
          const idx = state.list.findIndex(
            (item) => item.taskType === action.payload.taskType
          );
          if (idx >= 0) {
            state.list[idx].enabled = action.payload.enabled;
          }
        }
      )
      .addCase(setTaskVersionEnabled.rejected, (state, action) => {
        state.error = (action.payload as string) || '更新任务版本启用状态失败';
      });
  },
});

export default taskVersionSlice.reducer;


